import { useQuery } from '@tanstack/react-query';
import { createRoute } from '@tanstack/react-router';
import type { ReactNode } from 'react';

import {
  fetchBotInfoPublic,
  isSessionTerminatedCode,
  type SessionTerminatedCode,
} from '../api/client.ts';
import { buildBotDeepLink } from '../lib/bot-info.ts';
import { usePlatform } from '../platform/index.ts';

import { rootRoute } from './__root.tsx';

// Bot @username is immutable per deploy; cache hard so the sign-in deep-link
// is ready on first paint. Fetched tokenless because the session is already
// gone here — the normal token exchange would 403.
const BOT_INFO_STALE_MS = 24 * 60 * 60 * 1000;

// The reason carried here is one of the auth-exchange 403 codes. Two of them
// mean the user simply has not finished onboarding (no account row yet, or a
// row not yet linked to a backend account) — they were mid-flow in the bot,
// typically being asked for their email, and tapped into the mini app before
// answering. Those get a "finish in the bot" message rather than the generic
// "sign in again", which would wrongly imply they already have an account.
// ACCOUNT_INACTIVE (a blocked/deactivated account) keeps the sign-in-again
// copy. The code set itself is owned by the api client so it cannot drift.
export interface LoggedOutSearch {
  readonly reason?: SessionTerminatedCode;
}

const needsToFinishSignup = (reason: SessionTerminatedCode | undefined): boolean =>
  reason === 'ACCOUNT_NOT_FOUND' || reason === 'ACCOUNT_NOT_LINKED';

// Splash shown when the first token exchange fails because the session never
// opened (logout, a blocked account, or an un-onboarded user who jumped into
// the mini app mid-flow). The miniapp has no soft re-auth path: every recovery
// goes back through the bot chat, which the button reopens via a t.me
// deep-link (Telegram re-sends /start, and the bot resumes the email prompt
// for a user who was mid-onboarding). Falls back to just closing the WebApp
// when the bot username is unavailable.
const LoggedOutPage = (): ReactNode => {
  const platform = usePlatform();
  const { reason } = loggedOutRoute.useSearch();
  const finishSignup = needsToFinishSignup(reason);

  const botInfoQ = useQuery({
    queryKey: ['bot-info-public'],
    queryFn: ({ signal }) => fetchBotInfoPublic(signal),
    staleTime: BOT_INFO_STALE_MS,
    gcTime: BOT_INFO_STALE_MS,
    retry: 2,
  });
  const deepLink = buildBotDeepLink(botInfoQ.data?.username ?? '', 'relogin');

  const onSignIn = (): void => {
    // Open the bot chat (Telegram re-sends /start, and the bot re-asks email
    // for a user who was mid-onboarding), then close the mini app so the user
    // lands in the chat instead of behind a dead session screen. Open first so
    // the deep-link is handled before close.
    if (deepLink !== null) {
      platform.openBotLink(deepLink);
    }
    platform.close();
  };

  const title = finishSignup ? 'Almost there' : 'Session ended';
  let body: string;
  if (finishSignup) {
    body =
      deepLink !== null
        ? 'Head back to the chat and enter your email to finish signing up.'
        : 'Go back to the bot chat and enter your email to finish signing up.';
  } else {
    body =
      deepLink !== null
        ? 'Tap below to reopen the bot and sign in again.'
        : 'Go back to the bot chat and send /start to sign in again.';
  }
  let buttonLabel: string;
  if (deepLink === null) {
    buttonLabel = 'Close';
  } else {
    buttonLabel = finishSignup ? 'Back to the bot' : 'Sign in again';
  }

  return (
    <main className="logged-out-main" role="main">
      <div className="logged-out-card">
        <h1 className="logged-out-title">{title}</h1>
        <p className="logged-out-body">{body}</p>
        <button type="button" className="btn-primary logged-out-close" onClick={onSignIn}>
          {buttonLabel}
        </button>
      </div>
    </main>
  );
};

export const loggedOutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/logged-out',
  component: LoggedOutPage,
  validateSearch: (raw: Record<string, unknown>): LoggedOutSearch =>
    isSessionTerminatedCode(raw.reason) ? { reason: raw.reason } : {},
});
